import React from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { usePendingEvaluations } from 'queries/admin/coach/usePendingEvaluations'
import { useAssessmentsQueueFiltersQueryState } from '../hooks/useAssessmentsQueueFiltersQueryState'

const PAGE_SIZE = 20

type AssessmentsQueueFilters = ReturnType<
  typeof useAssessmentsQueueFiltersQueryState
>[0]

type PendingEvaluationsTableState = {
  items: NonNullable<
    ReturnType<typeof usePendingEvaluations>['data']
  >['pages'][number]['assessments']
  isPending: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  isError: boolean
  errorMessage: string | undefined
}

type AssessmentsQueueContextState = {
  state: {
    filters: AssessmentsQueueFilters
    upcoming: PendingEvaluationsTableState
    past: PendingEvaluationsTableState
  }
  actions: {
    setFilters: ReturnType<typeof useAssessmentsQueueFiltersQueryState>[1]
    loadMoreUpcoming: () => void
    loadMorePast: () => void
  }
}

const [AssessmentsQueueContext, useAssessmentsQueueContextValue] =
  createContext<AssessmentsQueueContextState>({
    name: 'AssessmentsQueueContext',
  })

const toTableState = (
  query: ReturnType<typeof usePendingEvaluations>
): PendingEvaluationsTableState => {
  const {
    data,
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    isError,
    error,
  } = query

  return {
    items: data?.pages.flatMap((page) => page.assessments) ?? [],
    isPending,
    isFetching,
    isFetchingNextPage,
    hasNextPage: Boolean(hasNextPage),
    isError,
    errorMessage:
      parseErrorMessage(error) ||
      (isError ? 'Failed to load pending evaluations.' : undefined),
  }
}

export const AssessmentsQueueProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters, setFilters] = useAssessmentsQueueFiltersQueryState()

  const requestFilters = {
    companyId: filters.companyId ?? undefined,
    positionId: filters.positionId ?? undefined,
    shiftId: filters.shiftId ?? undefined,
    pageSize: PAGE_SIZE,
  }

  const upcomingQuery = usePendingEvaluations({
    timeframe: 'upcoming',
    ...requestFilters,
  })
  const pastQuery = usePendingEvaluations({
    timeframe: 'past',
    ...requestFilters,
  })

  const value: AssessmentsQueueContextState = {
    state: {
      filters,
      upcoming: toTableState(upcomingQuery),
      past: toTableState(pastQuery),
    },
    actions: {
      setFilters,
      loadMoreUpcoming: () => {
        void upcomingQuery.fetchNextPage()
      },
      loadMorePast: () => {
        void pastQuery.fetchNextPage()
      },
    },
  }

  return (
    <AssessmentsQueueContext.Provider value={value}>
      {children}
    </AssessmentsQueueContext.Provider>
  )
}

export { useAssessmentsQueueContextValue }
